import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Mic, MicOff, AlertCircle, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from './button';

interface SessionStatusIndicatorProps {
  state: string;
  message?: string;
  showMicrophoneHint?: boolean;
  onRetry?: () => void;
  className?: string;
}

export function SessionStatusIndicator({
  state,
  message,
  showMicrophoneHint = true,
  onRetry,
  className
}: SessionStatusIndicatorProps) {
  const getStatusContent = () => {
    switch (state) {
      case 'initializing':
        return {
          icon: <Loader2 className="h-5 w-5 animate-spin" />,
          text: message || 'Setting up your coaching session...',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          micStatus: 'muted'
        };
      
      case 'avatar_ready':
        return {
          icon: <Loader2 className="h-5 w-5 animate-spin" />,
          text: message || 'Your coach is getting ready...',
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          micStatus: 'muted'
        };
      
      case 'avatar_speaking_first':
        return {
          icon: <div className="flex space-x-1">
            <motion.div
              className="w-1 h-4 bg-green-600 rounded-full"
              animate={{ scaleY: [1, 1.5, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
            <motion.div
              className="w-1 h-4 bg-green-600 rounded-full"
              animate={{ scaleY: [1, 1.5, 1] }}
              transition={{ duration: 0.5, repeat: Infinity, delay: 0.1 }}
            />
            <motion.div
              className="w-1 h-4 bg-green-600 rounded-full"
              animate={{ scaleY: [1, 1.5, 1] }}
              transition={{ duration: 0.5, repeat: Infinity, delay: 0.2 }}
            />
          </div>,
          text: message || 'Your coach is greeting you...',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          micStatus: 'muted'
        };
      
      case 'voice_ready':
        return {
          icon: <CheckCircle className="h-5 w-5" />,
          text: message || 'Microphone ready - you can speak now',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          micStatus: 'preparing'
        };
      
      case 'fully_active':
        return {
          icon: <CheckCircle className="h-5 w-5" />,
          text: message || 'Session active',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          micStatus: 'active'
        };
      
      case 'error':
        return {
          icon: <AlertCircle className="h-5 w-5" />,
          text: message || 'Connection issue - click to retry',
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          micStatus: 'error'
        };
      
      case 'retry':
        return {
          icon: <RefreshCw className="h-5 w-5 animate-spin" />,
          text: message || 'Reconnecting...',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          micStatus: 'muted'
        };
      
      default:
        return {
          icon: <Loader2 className="h-5 w-5 animate-spin" />,
          text: 'Initializing...',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          micStatus: 'muted'
        };
    }
  };

  const { icon, text, color, bgColor, micStatus } = getStatusContent();

  const getMicrophoneIcon = () => {
    switch (micStatus) {
      case 'muted':
        return <MicOff className="h-4 w-4 text-red-500" />;
      case 'preparing':
        return (
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Mic className="h-4 w-4 text-yellow-500" />
          </motion.div>
        );
      case 'active':
        return <Mic className="h-4 w-4 text-green-500" />;
      case 'error':
        return <MicOff className="h-4 w-4 text-red-500" />;
      default:
        return <MicOff className="h-4 w-4 text-gray-400" />;
    }
  };

  const isClickable = state === 'error' && onRetry;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={state}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className={cn(
          'inline-flex items-center gap-3 px-4 py-2 rounded-full',
          bgColor,
          isClickable && 'cursor-pointer hover:opacity-80 transition-opacity',
          className
        )}
        onClick={isClickable ? onRetry : undefined}
      >
        <span className={color}>{icon}</span>
        <span className={cn('text-sm font-medium', color)}>{text}</span>
        
        {showMicrophoneHint && state !== 'fully_active' && (
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-300">
            {getMicrophoneIcon()}
            <span className="text-xs text-gray-600">
              {micStatus === 'muted' && 'Mic disabled'}
              {micStatus === 'preparing' && 'Preparing...'}
              {micStatus === 'active' && 'Mic active'}
              {micStatus === 'error' && 'Mic error'}
            </span>
          </div>
        )}
        
        {isClickable && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-2 h-6 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onRetry?.();
            }}
          >
            Retry
          </Button>
        )}
      </motion.div>
    </AnimatePresence>
  );
}